"use client";

import * as RUITooltip from "@radix-ui/react-tooltip";
import { cloneElement, isValidElement, type PropsWithChildren, type ReactElement, type Ref, useRef } from "react";

import useIsOverflow from "../../hooks/useIsOverflow";
import { cn } from "../../utils";

import "./tooltip.css";

export interface TooltipRootProps {
  open?: boolean;
  defaultOpen?: boolean;
}

const Root = ({ children, defaultOpen, open }: PropsWithChildren<TooltipRootProps>) => {
  return (
    <RUITooltip.Root defaultOpen={defaultOpen} open={open}>
      {children}
    </RUITooltip.Root>
  );
};

export interface TooltipTriggerProps {
  asChild?: boolean;
  onOverflow?: boolean;
  className?: string;
  disabled?: boolean;
}

export const Trigger = ({
  children,
  onOverflow,
  className,
  disabled,
  asChild,
}: PropsWithChildren<TooltipTriggerProps>) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const isOverflow = useIsOverflow(triggerRef);
  const shouldDisabled = disabled || (onOverflow && !isOverflow);

  const content =
    asChild && isValidElement(children) ? (
      cloneElement(children as ReactElement<{ ref: Ref<HTMLDivElement> }>, { ref: triggerRef })
    ) : (
      <div role={shouldDisabled ? undefined : "button"} ref={triggerRef} className={className}>
        {children}
      </div>
    );

  if (isOverflow || !onOverflow) {
    return (
      <RUITooltip.Trigger className={className} asChild>
        {content}
      </RUITooltip.Trigger>
    );
  }

  return content;
};

export interface TooltipContentProps {
  className?: string;
  sideOffset?: number;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  theme?: "light" | "dark";
  alignOffset?: number;
  sticky?: "partial" | "always";
  onClickOutside?: (e: Event) => void;
  container?: Element;
  collisionBoundary?: Element | null | (Element | null)[];
}

export const Content = ({
  children,
  className,
  sideOffset = 5,
  alignOffset = 0,
  side = "right",
  align = "center",
  theme = "dark",
  sticky = "partial",
  onClickOutside,
  container,
  collisionBoundary,
}: PropsWithChildren<TooltipContentProps>) => {
  return (
    <RUITooltip.Portal container={container}>
      <RUITooltip.Content
        className={cn(
          "lighthouse-tooltip-content",
          "z-150 max-w-[350px] rounded-sm px-3 py-2 text-xs leading-[150%] font-semibold whitespace-pre-wrap shadow-lg",
          theme === "dark" ? "bg-neutral-10 text-white" : "bg-white text-neutral-30",
          className
        )}
        sideOffset={sideOffset}
        alignOffset={alignOffset}
        side={side}
        align={align}
        sticky={sticky}
        collisionBoundary={collisionBoundary}
        onPointerDownOutside={e => {
          onClickOutside?.(e);
        }}
      >
        {children}
      </RUITooltip.Content>
    </RUITooltip.Portal>
  );
};

const Provider = RUITooltip.Provider;

export interface TooltipArrowProps {
  className?: string;
  theme?: "light" | "dark";
}
export const Arrow = ({ theme = "dark", className }: TooltipArrowProps) => (
  <RUITooltip.Arrow className={cn("shadow-lg", theme === "dark" ? "fill-neutral-10" : "fill-white", className)} />
);

export const Tooltip = { Root, Trigger, Content, Arrow, Provider };
